import { useMutation } from '@apollo/client/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { CREATE_LOCATION_OVERRIDE } from '@optools/services/operator-gateway/ip-override/mutations';
import { GET_ACTIVE_LOCATION_OVERRIDES } from '@optools/services/operator-gateway/ip-override/queries';
import { ReactNode, useState } from 'react';
import { toast } from 'sonner';

import { OverrideForm, OverrideFormValues } from './override-form';

export interface CreateOverrideDialogProps {
  children: ({ onClick }: { onClick: () => void }) => ReactNode;
  override?: OverrideFormValues;
}

export function CreateOverrideDialog({ children, override }: CreateOverrideDialogProps) {
  const [open, setOpen] = useState(false);

  const [createLocationOverride, { loading }] = useMutation(CREATE_LOCATION_OVERRIDE, {
    onCompleted: ({ createLocationOverride }) => {
      if (createLocationOverride?.status !== 'ACCEPTED') {
        toast.error('Create Override Error', {
          description: 'Failed to create location override, please try again',
          duration: Infinity,
        });
        return;
      }

      toast.success('Override Created', { description: 'Location override successfully created' });
      setOpen(false);
    },
    refetchQueries: [{ query: GET_ACTIVE_LOCATION_OVERRIDES }],
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (loading) return;
    setOpen(nextOpen);
  };

  const handleSubmit = (values: OverrideFormValues) => {
    createLocationOverride({ variables: values });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children({ onClick: () => setOpen(true) })}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Override</DialogTitle>
          <DialogDescription className="sr-only">Create a new blackout override</DialogDescription>
        </DialogHeader>
        <OverrideForm
          loading={loading}
          override={override}
          onCancel={() => setOpen(false)}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}
